import { useRequest } from 'ahooks'
import { useId } from 'react'

import { asyncSuccessResolver, toast } from '$app/utilities'
import {
  errorMessageResolver,
  errorStatusResolver,
  successResolver
} from '$services/api/common'

import { FactorHookOptions } from './types'
import usePasswordVerifyBlock, {
  UsePasswordVerifyBlockOptions
} from '$actions/usePasswordVerifyForm'

type UsePasswordOptions = FactorHookOptions

export const usePassword = ({
  preferred_mfa_token_type = 'ott',
  apiInstance: { mfaChallengePasswordVerify },
  onVerified
}: UsePasswordOptions) => {
  const toastID = useId()

  const onApiError = (err: unknown) => {
    if (errorStatusResolver(err) === 401) {
      resetActions()
    }
  }

  const verifyReq = useRequest(
    asyncSuccessResolver(mfaChallengePasswordVerify, successResolver),
    {
      manual: true,
      onError: onApiError,
      onSuccess: res => onVerified(res.data.mfa_token)
    }
  )

  const requests = [verifyReq]

  const isAnyLoading = requests.map(({ loading }) => loading).includes(true)
  const disabled = isAnyLoading

  const onSuccess: UsePasswordVerifyBlockOptions['onSuccess'] = (
    resetForm,
    { password }
  ) => {
    const { runAsync, cancel } = verifyReq

    cancel()

    toast
      .promise(runAsync({ password, type: preferred_mfa_token_type }), {
        error: errorMessageResolver,
        loading: 'Verifying..',
        success: 'Verified.',
        id: toastID
      })
      .unwrap()
      .finally(resetForm)
  }

  const formProps = usePasswordVerifyBlock({
    title: 'Password Verification',
    description: 'Please enter your password to verify your identity.',
    submitButtonText: 'Verify',
    onSuccess,
    disabled
  })

  const resetActions = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
  }

  const reset = () => {
    resetActions()
    toast.dismiss(toastID)
  }

  return {
    isAnyLoading,
    reset,
    formProps
  }
}

export default usePassword
